import {Inject, Injectable} from '@angular/core';
import {catchError, filter, map, Observable, of, shareReplay, take, tap} from 'rxjs';

import {APP_URLS, AppUrls} from 'libs/shared/environment/src/lib/tokens/url.token';
import {ServiceManagerHttpService} from 'libs/shared/http/src/lib/services/http/http.service';
import {IdentityService, UserRole} from 'libs/shared/http/src/lib/services/identity/identity.service';

@Injectable({providedIn: 'root'})
export class ApexNavigationService {
    isApex$ = this.identityService.isInRole(UserRole.Apex).pipe(shareReplay(1));
    isApexMember$ = this.identityService.isInRole(UserRole.ApexMember).pipe(shareReplay(1));

    memberCanNavigate$ = this.isApexMember$.pipe(map((isApexMember) => isApexMember && !!this.entrypointUrl));

    private entrypointUrl: string | null = null;

    constructor(
        private httpService: ServiceManagerHttpService,
        @Inject(APP_URLS) private appUrls: AppUrls,
        private identityService: IdentityService
    ) {}

    setup(): Observable<void> {
        const setupEntrypoint$ = this.getEntrypointUrl().pipe(
            tap((entrypointUrl) => (this.entrypointUrl = entrypointUrl)),
            map((): void => undefined)
        );

        return setupEntrypoint$;
    }

    getFacultyCoursesLink(): string {
        return `${this.entrypointUrl}returnurl/courses/faculty/`;
    }

    getStudentGradeReportsLink(): string {
        return `${this.entrypointUrl}returnurl/student-reports/`;
    }

    getProgressReportLink(): string {
        return `${this.entrypointUrl}returnurl/progress-report/`;
    }

    navigateToApex(): void {
        this.isApex$
            .pipe(
                take(1),
                tap((isApex) => {
                    if (!isApex) {
                        return;
                    }

                    const url = `${this.entrypointUrl}returnurl/my-courses/`;
                    window.open(url, '_self');
                })
            )
            .subscribe();
    }

    navigateToOrders(): void {
        this.memberCanNavigate$
            .pipe(
                take(1),
                filter((canNavigate) => canNavigate),
                tap(() => {
                    const url = `${this.entrypointUrl}returnurl/my-account/orders/`;
                    window.open(url);
                })
            )
            .subscribe();
    }

    navigateToProducts(): void {
        this.memberCanNavigate$
            .pipe(
                take(1),
                filter((canNavigate) => canNavigate),
                tap(() => {
                    const url = `${this.entrypointUrl}returnurl/student/#products`;
                    window.open(url);
                })
            )
            .subscribe();
    }

    addProductToCart(ecomProductId: string): void {
        this.memberCanNavigate$
            .pipe(
                take(1),
                filter((canNavigate) => canNavigate),
                tap(() => {
                    const url = `${this.entrypointUrl}returnurl/add-to-cart/${ecomProductId}`;
                    window.open(url);
                })
            )
            .subscribe();
    }

    openAdminEditUser(externalAccountId: string): void {
        this.isApex$
            .pipe(
                take(1),
                filter((x) => x),
                tap(() => {
                    const url = `${this.entrypointUrl}returnurl/wp-admin-user-edit/${externalAccountId}`;
                    window.open(url);
                })
            )
            .subscribe();
    }

    navigateToStudentReviewCourse(): void {
        this.isApex$
            .pipe(
                take(1),
                filter((isApex) => isApex),
                tap(() => {
                    const url = `${this.entrypointUrl}returnurl/courses/student-review/`;
                    window.open(url, '_self');
                })
            )
            .subscribe();
    }

    private getEntrypointUrl(): Observable<string> {
        const path = '/account/apex/EntrypointUrl';

        return this.httpService.get<string>(path).pipe(catchError(() => of(null)));
    }
}
